import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { t as tt } from 'i18next'
import type * as Api from 'src/api'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import ProgressBar from 'src/views/components/ProgressBar'
import { type BulkMessageProgress } from './useBulkMessageProgress'

interface Props {
  readonly progress: BulkMessageProgress
  /** The send response for this batch; only known during the visit that sent it. */
  readonly summary: Api.AdministratorBulkMessage | null
  readonly onDismiss: () => void
}

export default function BulkMessageStatus({ progress, summary, onDismiss }: Props): JSX.Element {
  const t = useTranslatable()

  return (
    <div
      className={classNames('rounded-card px-6 py-5 text-primaryTextColor', {
        'bg-card': progress.status === 'loading' || progress.status === 'sending',
        'bg-lightGreen': progress.status === 'finished',
        'bg-lightYellow': progress.status === 'expired' || progress.status === 'stalled',
      })}
      data-testid="BulkMessageStatus"
    >
      {progress.status === 'expired' ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={ProRegularSvgIcons.faCircleInfo} />
            <span>{t('bulk_message:progress_expired')}</span>
          </div>
          <Button variant="outline" type="button" onClick={onDismiss}>
            {t('common:close')}
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 font-bold">
              <FontAwesomeIcon
                icon={
                  progress.status === 'finished' ? ProRegularSvgIcons.faCircleCheck : ProRegularSvgIcons.faPaperPlane
                }
              />
              {progress.status === 'finished' ? t('bulk_message:sending_finished') : t('bulk_message:sending')}
            </div>
            {(progress.status === 'finished' || progress.status === 'stalled') && (
              <Button variant="outline" type="button" onClick={onDismiss}>
                {t('common:close')}
              </Button>
            )}
          </div>
          {summary != null && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span>
                {t('bulk_message:rows_in_file')}: {summary.totalRows}
              </span>
              <span>
                {t('bulk_message:recipients_count')}: {summary.dispatchedCount}
              </span>
              <span>
                {t('bulk_message:skipped_rows')}: {summary.unmatched.length}
              </span>
            </div>
          )}
          {progress.status === 'loading' ? (
            <ProgressBar className="mt-4" label={t('bulk_message:sending')} percent={0} />
          ) : (
            <ProgressBar
              className="mt-4"
              label={tt('bulk_message:sent_of_total', { processed: progress.processed, total: progress.total })}
              percent={progress.total > 0 ? (progress.processed / progress.total) * 100 : 100}
            />
          )}
          {progress.status === 'stalled' && <p className="mt-3 text-sm">{t('bulk_message:progress_stalled')}</p>}
          {(progress.status === 'loading' || progress.status === 'sending') && (
            <p className="mt-3 text-sm text-captionColor">{t('bulk_message:wait_for_sending')}</p>
          )}
        </>
      )}
    </div>
  )
}
